import React from "react";
import { ShieldStar, FirstAidKit, GraduationCap, Warning, BookOpen, UsersThree } from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const PILLARS = [
  { icon: GraduationCap, title: "Continuous Training", body: "Every teammate completes onboarding, OSHA, and HazCom modules — refreshed annually with documented sign-offs." },
  { icon: FirstAidKit, title: "Bloodborne Pathogens", body: "OSHA 1910.1030 compliant program with exposure control plan, PPE, and post-exposure protocols." },
  { icon: ShieldStar, title: "PPE Standards", body: "Site-specific PPE matrices, monthly stock audits, and N95 fit-testing for medical accounts." },
  { icon: Warning, title: "Hazard Communication", body: "GHS-aligned SDS binders, container labeling, and chemical-incompatibility training across all sites." },
  { icon: BookOpen, title: "Documentation & QASP", body: "Inspection logs, deficiency tracking, corrective-action records — audit-ready at any moment." },
  { icon: UsersThree, title: "Site-Specific Plans", body: "Each account has a job hazard analysis, lockout/tagout map, and emergency action plan." },
];

const FAQS = [
  { q: "Are your crews background-checked?", a: "Yes. All teammates clear a 7-year background check and a federal sex-offender registry check before site assignment. Higher clearances (DCSA, NACI, Tier 1) available for federal contracts." },
  { q: "Do you carry HIPAA training for medical accounts?", a: "Yes. Every team member on a medical account completes HIPAA awareness training annually and signs a confidentiality acknowledgment. We maintain training records for client audits." },
  { q: "What is your incident rate?", a: "Our 3-year average OSHA TRIR is below the BSCAI industry benchmark. Detailed safety metrics provided in RFP responses." },
  { q: "How do you handle bloodborne pathogen exposures?", a: "Per 29 CFR 1910.1030: post-exposure evaluation, source-individual testing where consented, Hepatitis B vaccination offered, and a documented incident report shared with the facility." },
  { q: "Are chemicals OSHA HazCom 2012 compliant?", a: "Yes. Every chemical we use ships with a GHS-aligned SDS, secondary containers are labeled, and crews carry digital SDS access on their devices." },
];

export default function Safety() {
  return (
    <>
      <SEO title="Safety & Compliance" description="OSHA-trained crews, bloodborne pathogens program, HazCom, HIPAA awareness, and audit-ready QASP." path="/safety" />
      <PageHero
        eyebrow="Safety & Compliance"
        title="Safety is a system — not a slogan."
        subtitle="OSHA-aligned playbooks, written programs, and continuous training across every site."
        image="https://images.pexels.com/photos/8273515/pexels-photo-8273515.jpeg"
        testId="safety-hero"
      />

      <section className="es-section bg-white">
        <div className="es-container">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => (
              <div key={p.title} data-testid={`safety-pillar-${i}`} className="es-card p-7">
                <p.icon size={28} weight="duotone" className="text-[var(--es-accent)]" />
                <h3 className="mt-4 text-lg font-bold text-[var(--es-navy)]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{p.title}</h3>
                <p className="mt-2 text-sm text-[var(--es-text-muted)] leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="es-section bg-[var(--es-bg)]">
        <div className="es-container grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="es-eyebrow">Compliance FAQ</div>
            <h2 className="es-h2 text-[var(--es-navy)] mt-3">Answers your procurement & EHS teams are looking for.</h2>
            <p className="es-lede mt-4">Don't see your question? Ask in your RFP — we respond inside one business day.</p>
          </div>
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="bg-white border border-[var(--es-border)] rounded-sm" data-testid="safety-faq">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`} className="border-b border-[var(--es-border)] last:border-0">
                  <AccordionTrigger data-testid={`safety-faq-trigger-${i}`} className="px-5 py-4 text-left text-[var(--es-navy)] font-semibold hover:no-underline" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-4 text-sm text-[var(--es-text-muted)] leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
